import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ZoomIn } from 'lucide-react';
import { projects, categories } from '../data/galleryData';

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-[#4ade80]/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-[#4ade80]">Gallery</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of completed projects showcasing our quality workmanship and attention to detail
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-[#4ade80] text-black'
                    : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#1a1a1a]/80 border border-gray-800'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProjects.map((project, index) => (
              <Dialog key={index}>
                <DialogTrigger asChild>
                  <Card className="bg-[#1a1a1a] border-gray-800 overflow-hidden cursor-pointer group hover:border-[#4ade80] transition-all duration-300 hover-lift">
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <ZoomIn className="text-white" size={32} />
                      </div>
                      <Badge className="absolute top-3 right-3 bg-[#4ade80] text-black hover:bg-[#22c55e]">
                        {project.category}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="text-white font-semibold">{project.title}</h3>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent className="max-w-4xl bg-[#1a1a1a] border-gray-800">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-auto rounded-lg"
                  />
                  <div className="mt-4">
                    <Badge className="bg-[#4ade80] text-black mb-2">
                      {project.category}
                    </Badge>
                    <h3 className="text-2xl font-bold text-white">{project.title}</h3>
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Own Project?
          </h2>
          <p className="text-gray-300 text-lg mb-8 leading-relaxed">
            Let us bring your vision to life with the same quality and attention to detail you see in our gallery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-3 bg-[#4ade80] hover:bg-[#22c55e] text-black font-semibold rounded-lg transition-colors"
            >
              Get Free Quote
            </a>
            <a
              href="tel:07738965968"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white hover:bg-white hover:text-black rounded-lg font-semibold transition-colors"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;